import * as XLSX from 'xlsx';
import moment from 'moment';
import authService from 'services/auth.service';
import { STATUS_CODE, CONSTANTS, ACTION_CODES } from './constants';
import { AxiosResponse } from 'axios';
import { BaseResponseListModel, ResponseModelNew } from 'models/response.model';
import { UseQueryResult } from 'react-query';
import { Config } from 'models/test-detail.model';
import { LoginDataModel } from 'models/account.model';

// 1. UUID generator
export function UUID4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}


// 2. Deep copy object
export function deepCopyObj<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// 3. Export Excel
export function ExportExcel(data: any[], fileName: string, headers: any, hiddenCols?: any, columnWidths?: any): void {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet([]);
  if (headers?.length) {
    XLSX.utils.sheet_add_aoa(worksheet, [headers]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A2', skipHeader: true });
  } else {
    XLSX.utils.sheet_add_json(worksheet, data);
  }
  if (columnWidths.length) worksheet['!cols'] = columnWidths;
  hiddenCols.forEach((colIndex: any) => {
    worksheet['!cols']![colIndex] = { ...(worksheet['!cols']?.[colIndex] || {}), hidden: true };
  });
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, fileName);
}

// 4. Remove Vietnamese Tones
export function removeVietnameseTones(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
}

// 5. Convert base64 image to File
export function getFileFromBase64(base64: string, filename: string): File {
  const base64Data = base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new File([bytes], filename, { type: 'image/png', lastModified: Date.now() });
}

// 6. Calculate time diff in minutes
export function getMinDiff(start: number, end: number): number {
  return Math.round(Math.abs(end - start) / 60000);
}

// 7. Extract text from HTML
export function extractContentFromHtmlString(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

// 8. Capitalize first letter
export function capitalizeFirstLetter(str: string): string {
  return str.replace(/(^|\s)\S/g, (char) => char.toUpperCase());
}

// 9. Parse JWT
export function parseJwt(token: string): any {
  const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const decoded = atob(payload);
  const json = decodeURIComponent(
    decoded
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
}

// 10. Subtract array
export function subtractArray(arr1: any[], arr2: any[]): any[] {
  return arr1.filter(x => !arr2.includes(x));
}

// 11. Get evaluation feedback based on score
export function getEvaluateSendEmail(total: number, score: number): [string, string] {
  const percent = Math.round((score / total) * 100);
  if (percent > 95) return ["Con hoàn thành xuất sắc bài thi. Con giỏi lắm, chúc mừng con!", "Xuất sắc"];
  if (percent > 80) return ["Con làm bài thi giỏi lắm. Tuy còn sai một chút nhỏ, nhưng đã rất tốt. Chúc mừng con!", "Giỏi"];
  if (percent > 60) return ["Bài thi sai hơi nhiều, còn cần chú ý hơn. Chúc mừng con!", "Khá"];
  return ["Bài thi sai nhiều quá, con cần phải cố gắng thật nhiều hơn nữa để đạt kết quả tốt hơn!", "Trung bình"];
}

// 12. Shuffle array
export function shuffle<T>(array: T[]): T[] {
  let currentIndex = array.length, randomIndex: number;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }
  return array;
}

// 13. XOREncryptDecrypt
export function XOREncryptDecrypt(input: string, key: string = 'tantanotnt96'): string {
  let result = '';
  for (let i = 0; i < input.length; i++) {
    result += String.fromCharCode(input.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
}

export function GetLoginAccountId(): string {
  const user = localStorage.getItem('HHQ1_LOGIN_DATA');
  if (!user) return '';
  try {
    const parsed = JSON.parse(user);
    return parsed?.id || '';
  } catch {
    return '';
  }
}

export const GetLoginData = (): LoginDataModel | null  => {
    const hasLoginKey = CONSTANTS.LOGIN_DATA;

    if (hasLoginKey) {
        return authService.loginData();
    }

    return null;
};

export const CheckAdminRole = (): boolean => {
    const data = GetLoginData();

    return !!(
        data &&
        Array.isArray(data.roles) &&
        data.roles.some(role => role.role_id === 'admin')
    );
};

export const checkActionPermision = (pathname: string, autoDisplay: boolean = false): Record<string, boolean> => {
  const permissions: Record<string, boolean> = {};
  type ActionCodeKey = keyof typeof ACTION_CODES;
  // Xoá dấu "/" cuối nếu có
  if (pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1);
  }

  if (autoDisplay) {
    // Nếu là tự động hiển thị, gán tất cả action = true

    for (const code of Object.keys(ACTION_CODES) as ActionCodeKey[]) {
      permissions[ACTION_CODES[code]] = true; // OK
    }
  } else {
    const roleData = localStorage.getItem(CONSTANTS.LOGIN_ROLES);
    if (roleData) {
      const parsedRoles = JSON.parse(roleData);
      const matchedRole = parsedRoles.find((role: any) => role.path === pathname);
      if (matchedRole) {
        const actions = JSON.parse(matchedRole.actions || '[]');
        for (const code of Object.keys(ACTION_CODES) as ActionCodeKey[]) {
          const found = actions.some((a: any) => a.actionId === ACTION_CODES[code]);
          permissions[ACTION_CODES[code]] = found;
        }
      }
    }
  }

  return permissions;
};

export function CheckMenuExitst(menu: any, field: string, value: any): any {
    if (menu[field] === value) return menu;

    if (!menu.items) return null;

    for (let i = 0; i < menu.items.length; i++) {
        const result = CheckMenuExitst(menu.items[i], field, value);
        if (result) return result;
    }

    return null;
}

export function FindMenuInListByPath(menus: any[], field: string, value: any): any {
    for (let i = 0; i < menus.length; i++) {
        const found = CheckMenuExitst(menus[i], field, value);
        if (found) return found;
    }
    return null;
}


export function getListResponse<T>(
    data: UseQueryResult<AxiosResponse<BaseResponseListModel<T[]>>, unknown>
): T[] {
    if (
        data &&
        data.isSuccess &&
        data.data?.data &&
        Array.isArray(data.data.data.data.data) &&
        data.data.data.data.statusCode === STATUS_CODE.SUCCESS
    ) {
        return data.data.data.data.data;
    }

    return [];
}


export function getDetailResponse<T>(
    data: UseQueryResult<AxiosResponse<ResponseModelNew<T>>, unknown>
): T {
    if (
        data &&
        data.isSuccess &&
        data.data?.data &&
        data.data?.data.statusCode === STATUS_CODE.SUCCESS
    ) {
        return data.data.data.data;
    }

    return {} as T;
}


export function getTotalRecord<T>(
  data: UseQueryResult<AxiosResponse<BaseResponseListModel<T[]>>, unknown>
): number | undefined {
  return data.data?.data?.data?.recordsTotal;
}


export const RemoveMultipleFieldInObject = (object: any, keys: string[]): any => {
    const result = { ...object };

    keys.forEach((key) => {
        delete result[key];
    });

    return result;
};

export const formatDateView = (date: string | null): string | null => {
    if (!date) return null;

    const formatted = moment(date).format('DD-MM-YYYY HH:mm:ss');
    return formatted === 'Invalid date' ? null : formatted;
};

export function getCommentConfig(configs: Config[], percent: number): string {
    for (const config of configs) {
        const from = typeof config.from === 'string' ? parseFloat(config.from) : config.from;

        if (percent >= from && percent <= config.to) {
            return config.comment;
        }
    }

    return '';
}


export function consoleClearProd(): void {
  if (process.env.NODE_ENV === 'production') {
    console.clear();
  }
}

export function isProdMode(): boolean {
  return process.env.NODE_ENV === 'production';
}
